import type { RefObject } from 'react';
import { useEffect } from 'react';

/**
 * A hook that prevents the body from scrolling when a condition is met (e.g., a dialog is open).
 * It allows scrolling on a specific designated element while preventing that scroll from chaining to the body.
 *
 * @param scrollableElementRef A ref to the element that should remain scrollable (e.g., Dialog.Body).
 * @param enabled A boolean to turn the scroll lock on or off.
 */
export const usePreventScrollChain = (
  scrollableElementRef: RefObject<HTMLElement>,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const scrollableElement = scrollableElementRef.current;
    if (!scrollableElement) {
      return;
    }

    const isAtScrollBoundary = (deltaY: number) => {
      const { scrollTop, scrollHeight, clientHeight } = scrollableElement;
      const isAtTop = deltaY < 0 && scrollTop <= 0;
      const isAtBottom = deltaY > 0 && scrollTop + clientHeight >= scrollHeight - 1;

      return isAtTop || isAtBottom;
    };

    const handleWheel = (event: WheelEvent) => {
      if (isAtScrollBoundary(event.deltaY)) {
        event.preventDefault();
      }
    };

    let lastTouchY = 0;

    const handleTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? 0;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const currentTouchY = event.touches[0]?.clientY ?? lastTouchY;
      const deltaY = lastTouchY - currentTouchY;

      if (isAtScrollBoundary(deltaY)) {
        event.preventDefault();
      }

      lastTouchY = currentTouchY;
    };

    scrollableElement.addEventListener('wheel', handleWheel, { passive: false });
    scrollableElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollableElement.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      scrollableElement.removeEventListener('wheel', handleWheel);
      scrollableElement.removeEventListener('touchstart', handleTouchStart);
      scrollableElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, [enabled, scrollableElementRef]);
};

export default usePreventScrollChain;
